/**
 * Маппер живого ClientDetailResponse → доменный ClientDetail прототипа.
 * Гейтвей отдаёт карточку целиком (строка списка + детали) — здесь берём
 * только детальную часть; платежи приходят в тыйынах, показываем сомы.
 */

import type {
  AiMode,
  ClientDetail,
  ClientPayment,
  PaymentStatus,
  StaffMember,
  WhatsAppBinding,
} from '../types';

const asStr = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const asNum = (v: unknown, fallback = 0): number => (typeof v === 'number' ? v : fallback);
const asObj = (v: unknown): Record<string, unknown> =>
  (typeof v === 'object' && v !== null ? v : {}) as Record<string, unknown>;

const asPaymentStatus = (v: unknown): PaymentStatus =>
  v === 'failed' || v === 'refunded' ? v : 'success';

const AI_MODES: AiMode[] = ['off', 'shadow', 'assisted', 'auto'];

/** Незнакомый режим считаем выключенным: это самое безобидное из четырёх. */
const asAiMode = (v: unknown): AiMode => (AI_MODES.includes(v as AiMode) ? (v as AiMode) : 'off');

/** Привязки может не быть вовсе — это нормальное состояние, а не ошибка. */
const toWhatsApp = (raw: unknown): WhatsAppBinding | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  return {
    phoneNumberId: asStr(r.phoneNumberId),
    displayPhone: asStr(r.displayPhone),
    wabaId: typeof r.wabaId === 'string' && r.wabaId ? r.wabaId : null,
    mode: r.mode === 'coexistence' ? 'coexistence' : 'api_only',
    active: r.active !== false,
  };
};

const toStaffMember = (raw: unknown): StaffMember => {
  const r = asObj(raw);
  return {
    id: asStr(r.id),
    accountId: asStr(r.accountId),
    fullName: asStr(r.fullName, '—'),
    phone: asStr(r.phone),
    role: r.role === 'admin' ? 'admin' : 'doctor',
    specialization: typeof r.specialization === 'string' ? r.specialization : null,
    active: r.active !== false,
    blocked: r.blocked === true,
  };
};

const toPayment = (raw: unknown): ClientPayment => {
  const r = asObj(raw);
  return {
    id: asStr(r.id),
    label: asStr(r.label, 'Платёж'),
    date: asStr(r.date),
    amount: Math.round(asNum(r.amountTiyin) / 100),
    status: asPaymentStatus(r.status),
  };
};

export const toClientDetail = (raw: unknown): ClientDetail => {
  const r = asObj(raw);
  const activity = asObj(r.activity);
  return {
    id: asStr(r.id),
    address: typeof r.address === 'string' ? r.address : null,
    businessPhone: typeof r.businessPhone === 'string' ? r.businessPhone : null,
    language: asStr(r.language, 'Русский'),
    cardAttached: r.cardAttached === true,
    specialization: typeof r.specialization === 'string' ? r.specialization : null,
    activity:
      r.activity != null
        ? {
            patients: asNum(activity.patients),
            visits: asNum(activity.visits),
            lastLogin: asStr(activity.lastLogin, '—'),
          }
        : null,
    staff: (Array.isArray(r.staff) ? r.staff : []).map(toStaffMember),
    payments: (Array.isArray(r.payments) ? r.payments : []).map(toPayment),
    notes: asStr(r.notes),
    adminBlocked: r.adminBlocked === true,
    whatsapp: toWhatsApp(r.whatsapp),
    aiMode: asAiMode(r.aiMode),
    aiModeReason: typeof r.aiModeReason === 'string' && r.aiModeReason ? r.aiModeReason : null,
    aiModeChangedAt: typeof r.aiModeChangedAt === 'string' ? r.aiModeChangedAt : null,
  };
};
